import React, { useEffect, useLayoutEffect } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material";
import { observer } from "mobx-react";
import ContentSnackbar from "../snackbars/Content";
import { NumericFormat } from "react-number-format";
import { useStores } from "../../stores";
import { GetQuery } from "../../models/Souvenir";
import NumberFieldWithButton from "../inputs/NumberFieldWithButton";
import StyleVariables from "../../assets/styles/Variable.module.scss";

type Props = {
  query: GetQuery;
  contextKey?: string;
};

const Souvenir = observer(({ query, contextKey = "" }: Props) => {
  const { SouvenirStore } = useStores();
  const { transactionType, weight, price, labour, interest } = query;

  useLayoutEffect(() => {
    SouvenirStore.onSetQuery(
      {
        transactionType,
        weight,
        price,
        labour,
        interest,
      },
      contextKey
    );
  }, [
    SouvenirStore,
    transactionType,
    weight,
    price,
    labour,
    interest,
    contextKey,
  ]);

  useEffect(() => {
    return () => {
      SouvenirStore.onClearView();
    };
  }, [SouvenirStore]);

  const pending = SouvenirStore.currentPending;
  const selectedNames = SouvenirStore.items
    .filter((item) => item.selectedQuantity > 0)
    .map((item) => item.souvenirName);

  const columns = {
    xs: "minmax(0, 1fr)",
    sm: "repeat(2, minmax(0, 1fr))",
    md: "minmax(0, 1fr) 140px 240px",
  };
  return (
    <>
      {pending && (
        <Box
          padding={3}
          marginBottom={2}
          borderRadius={2}
          bgcolor={StyleVariables["color-warning-background"]}
          sx={{
            borderLeft: `4px solid ${StyleVariables["color-warning"]}`,
          }}
        >
          <Typography
            variant="body1"
            fontWeight={"bold"}
            color={StyleVariables["color-header"]}
            marginBottom={1}
          >
            รายการนี้ยังดำเนินการไม่ครบ
          </Typography>

          <Typography
            variant="body2"
            color={StyleVariables["color-text"]}
            sx={{ overflowWrap: "anywhere", lineHeight: 1.8 }}
          >
            {pending.message || `รายการ ${pending.transactionId} บันทึกแล้ว`}
          </Typography>

          <Typography
            variant="body2"
            color={StyleVariables["color-subheader"]}
            marginTop={1}
          >
            การกดเริ่มใหม่จะไม่ยกเลิกรายการที่บันทึกไปแล้ว
          </Typography>
        </Box>
      )}

        <Box>
          {SouvenirStore.isLoading ? (
            <Box
              display={"flex"}
              flexDirection={"column"}
              alignItems={"center"}
              justifyContent={"center"}
              gap={2}
              paddingY={6}
              role="status"
            >
              <CircularProgress size={32} />

              <Typography
                variant="body2"
                color={StyleVariables["color-subheader"]}
              >
                กำลังโหลดข้อมูลของแถม
              </Typography>
            </Box>
          ) : SouvenirStore.errorMessage ? (
            <Box
              padding={3}
              borderRadius={2}
              bgcolor={StyleVariables["color-error-light"]}
              role="alert"
            >
              <Typography
                variant="body1"
                fontWeight={"bold"}
                color={StyleVariables["color-error"]}
              >
                โหลดข้อมูลของแถมไม่สำเร็จ
              </Typography>

              <Typography
                variant="body2"
                color={StyleVariables["color-text"]}
                marginTop={1}
                sx={{ overflowWrap: "anywhere" }}
              >
                {SouvenirStore.errorMessage}
              </Typography>

              {selectedNames.length > 0 && (
                <Typography
                  variant="body2"
                  color={StyleVariables["color-subheader"]}
                  marginTop={1}
                >
                  เก็บรายการที่เลือกไว้แล้ว
                  กรุณาโหลดข้อมูลใหม่เพื่อตรวจสอบก่อนทำรายการ
                </Typography>
              )}

              <Button
                variant="outlined"
                sx={{
                  marginTop: 3,
                  width: { xs: "100%", sm: "auto" },
                }}
                onClick={() => {
                  void SouvenirStore.onExpandedChange(true);
                }}
              >
                โหลดข้อมูลใหม่
              </Button>
            </Box>
          ) : (
            <>
              {SouvenirStore.noticeMessage && (
                <Box
                  role="status"
                  aria-live="polite"
                  marginBottom={2}
                >
                  <ContentSnackbar
                    type="warning"
                    title={SouvenirStore.noticeMessage}
                    fontSize={22}
                  />
                </Box>
              )}

              {!SouvenirStore.items.length ? (
                <Box
                  paddingX={3}
                  paddingY={5}
                  textAlign={"center"}
                  borderRadius={2}
                  bgcolor={StyleVariables["color-background"]}
                >
                  <Typography
                    variant="body1"
                    fontWeight={"bold"}
                    color={StyleVariables["color-header"]}
                  >
                    ไม่มีของแถมตามเงื่อนไขรายการนี้
                  </Typography>

                  <Typography
                    variant="body2"
                    color={StyleVariables["color-subheader"]}
                    marginTop={1}
                  >
                    สามารถทำรายการต่อได้โดยไม่เลือกของแถม
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <Box
                    display={{ xs: "none", md: "grid" }}
                    gridTemplateColumns={columns}
                    gap={3}
                    paddingLeft={0}
                    paddingRight={3}
                    paddingY={1.5}
                    marginBottom={1}
                  >
                    <Typography
                      variant="body2"
                      fontWeight={"bold"}
                      color={StyleVariables["color-subheader"]}
                    >
                      รายการของแถม
                    </Typography>

                    <Typography
                      variant="body2"
                      fontWeight={"bold"}
                      textAlign={"center"}
                      color={StyleVariables["color-subheader"]}
                    >
                      คงเหลือ
                    </Typography>

                    <Typography
                      variant="body2"
                      fontWeight={"bold"}
                      textAlign={"center"}
                      color={StyleVariables["color-subheader"]}
                    >
                      จำนวน
                    </Typography>
                  </Box>

                  <Box display={"flex"} flexDirection={"column"} gap={2}>
                    {SouvenirStore.items.map((item) => {
                      const canIncrease = SouvenirStore.canIncrease(
                        item.souvenirCode
                      );
                      const canDecrease = SouvenirStore.canDecrease(
                        item.souvenirCode
                      );
                      const isSelected = item.selectedQuantity > 0;
                      const isOutOfStock = item.quantity === 0;
                      const isFullySelected =
                        isSelected &&
                        item.selectedQuantity === item.quantity;
                      const tagText = isOutOfStock
                        ? "หมด"
                        : isFullySelected
                        ? "ครบแล้ว"
                        : isSelected
                        ? "เลือกแล้ว"
                        : "";

                      return (
                        <Box
                          key={item.souvenirCode}
                          display={"grid"}
                          gridTemplateColumns={columns}
                          alignItems={{ xs: "start", md: "center" }}
                          gap={{ xs: 2, sm: 3 }}
                          padding={{ xs: 2, md: 3 }}
                          borderRadius={2}
                          bgcolor={
                            isSelected
                              ? StyleVariables["color-hovered"]
                              : StyleVariables["color-white"]
                          }
                          sx={{
                            border: `1px solid ${StyleVariables["color-border"]}`,
                          }}
                        >
                          <Box
                            minWidth={0}
                            gridColumn={{
                              xs: "1 / -1",
                              md: "auto",
                            }}
                          >
                            <Box
                              display={"flex"}
                              alignItems={"center"}
                              flexWrap={"wrap"}
                              gap={1}
                            >
                              <Typography
                                variant="h6"
                                fontWeight={"bold"}
                                color={StyleVariables["color-header"]}
                                title={item.souvenirName}
                                sx={{
                                  minWidth: 0,
                                  maxWidth: "100%",
                                  overflowWrap: "anywhere",
                                  display: "-webkit-box",
                                  WebkitBoxOrient: "vertical",
                                  WebkitLineClamp: 2,
                                  overflow: "hidden",
                                }}
                              >
                                {item.souvenirName}
                              </Typography>

                              {tagText && (
                                <Box
                                  display={"inline-flex"}
                                  alignItems={"center"}
                                  paddingX={1.5}
                                  paddingY={0.5}
                                  borderRadius={1}
                                  flexShrink={0}
                                  bgcolor={
                                    isOutOfStock
                                      ? StyleVariables["color-error-tag"]
                                      : StyleVariables["color-success-tag"]
                                  }
                                >
                                  <Typography
                                    variant="body2"
                                    fontWeight={"bold"}
                                    color={
                                      isOutOfStock
                                        ? StyleVariables["color-error"]
                                        : StyleVariables["color-success"]
                                    }
                                    sx={{ whiteSpace: "nowrap" }}
                                  >
                                    {tagText}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </Box>

                          {!isOutOfStock && (
                            <>
                              <Box
                                minWidth={0}
                                textAlign={{ xs: "left", md: "center" }}
                              >
                                <Typography
                                  variant="body2"
                                  color={StyleVariables["color-subheader"]}
                                  display={{ xs: "block", md: "none" }}
                                  marginBottom={1}
                                >
                                  คงเหลือ
                                </Typography>

                                <Typography
                                  variant="body1"
                                  fontWeight={"bold"}
                                  color={StyleVariables["color-header"]}
                                  sx={{
                                    minHeight: { xs: "auto", sm: 64 },
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: {
                                      xs: "flex-start",
                                      md: "center",
                                    },
                                    flexWrap: "wrap",
                                    columnGap: 0.5,
                                    fontVariantNumeric: "tabular-nums",
                                    overflowWrap: "anywhere",
                                  }}
                                >
                                  <NumericFormat
                                    value={item.quantity}
                                    displayType="text"
                                    thousandSeparator
                                    decimalScale={0}
                                  />
                                  {item.unit ? ` ${item.unit}` : ""}
                                </Typography>
                              </Box>

                              <Box minWidth={0}>
                                <Typography
                                  variant="body2"
                                  color={StyleVariables["color-subheader"]}
                                  display={{ xs: "block", md: "none" }}
                                  marginBottom={1}
                                >
                                  จำนวน
                                </Typography>

                                <Box
                                  maxWidth={280}
                                  width={"100%"}
                                  minWidth={0}
                                  sx={{
                                    "& > .MuiBox-root": {
                                      display: "grid",
                                      gridTemplateColumns:
                                        "40px minmax(0, 1fr) 40px",
                                      alignItems: "center",
                                      gap: 1,
                                      width: "100%",
                                    },
                                    "& > .MuiBox-root > .MuiSvgIcon-root": {
                                      marginTop: 0,
                                      fontSize: 40,
                                      width: 40,
                                      height: 40,
                                    },
                                    "& > .MuiBox-root > .MuiBox-root": {
                                      minWidth: 0,
                                      width: "100%",
                                    },
                                    "& .MuiInputBase-root": {
                                      boxSizing: "border-box",
                                      width: "100%",
                                      minWidth: 0,
                                      height: 64,
                                      padding: 0,
                                    },
                                    "& .MuiInputBase-input": {
                                      boxSizing: "border-box",
                                      width: "100%",
                                      minWidth: 0,
                                      height: "100%",
                                      padding: "8px",
                                      textAlign: "center",
                                      fontSize: "28px",
                                      lineHeight: "40px",
                                      fontWeight: 700,
                                      fontVariantNumeric: "tabular-nums",
                                    },
                                    "& input.Mui-disabled": {
                                      WebkitTextFillColor:
                                        StyleVariables["color-header"],
                                    },
                                  }}
                                >
                                  <NumberFieldWithButton
                                    name={`souvenir-${item.souvenirCode}`}
                                    label=""
                                    placeholder="0"
                                    value={item.selectedQuantity}
                                    status=""
                                    isDisabled={true}
                                    rangeOfIncrease={1}
                                    rangeOfDecrease={1}
                                    isIncreaseDisabled={!canIncrease}
                                    isDecreaseDisabled={!canDecrease}
                                    handleOnChange={(value) => {
                                      SouvenirStore.onQuantityChange(
                                        item.souvenirCode,
                                        Number(value)
                                      );
                                    }}
                                    handleOnIncrease={(value) => {
                                      SouvenirStore.onQuantityChange(
                                        item.souvenirCode,
                                        Number(value)
                                      );
                                    }}
                                    handleOnDecrease={(value) => {
                                      SouvenirStore.onQuantityChange(
                                        item.souvenirCode,
                                        Number(value)
                                      );
                                    }}
                                  />
                                </Box>
                              </Box>
                            </>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}
            </>
          )}
        </Box>
    </>
  );
});

export default Souvenir;